import { useMemo, useState } from 'react'
import { useTranslation } from '@kostbar/i18n'
import {
  CalendarRange,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
} from 'lucide-react'
import {
  PeriodGrainGrid,
  pageCursor,
  pageIsAtPresent,
  weekWindow,
} from './period-grain-grid'
import type {
  CustomRange,
  NaturalPeriod,
  PeriodGrain,
  PeriodPreset,
} from '@/features/reports/lib/period'
import {
  NATURAL_PERIODS,
  describeRange,
  isWholeDayRange,
  isoWeekOf,
  naturalPeriodHint,
  resolveNaturalPeriod,
} from '@/features/reports/lib/period'
import {
  reportDate,
  reportDaySpan,
  shiftReportDate,
} from '@/features/reports/lib/report-zone'
import { formatDate } from '@/features/reports/lib/format'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export type { CustomRange } from '@/features/reports/lib/period'

const GRAINS: Array<PeriodGrain> = ['day', 'week', 'month', 'quarter']

/** Offered per end of the range; anything else is typed into the field beside them. */
const FROM_TIMES = ['00:00', '06:00', '11:00']
const TO_TIMES = ['14:00', '20:00', '24:00']

interface PeriodSwitcherProps {
  value: PeriodPreset
  custom?: CustomRange | null
  onChange: (preset: PeriodPreset, custom?: CustomRange) => void
  /**
   * Whether a Tageszeit filter is on the same surface. The time rows below explain themselves by
   * contrast with it — "durchgehende Spanne, *nicht* an jedem Tag nur diese Stunden" — and on the
   * dashboard, the one surface that deliberately has no Tageszeit filter, that sentence sent the
   * reader hunting for a control that is not there. The distinction still has to be drawn; only
   * the pointer goes.
   */
  hasTimeWindowFilter?: boolean
  /**
   * Whether the four length presets sit beside this control. When they do, the trigger names
   * itself — "Zeitraum wählen" — instead of restating the period: two controls both claiming to
   * state the current slice make the reader look for a difference between them that is not there.
   * A picked range still shows on the trigger, because then it IS the selection and the presets
   * beside it are empty.
   */
  hasPresetsBeside?: boolean
}

/**
 * The period — one control (CHG-2026-09-manager-reporting, reworked by
 * CHG-2026-09-smarter-period-picker).
 *
 * **One trigger, not a row of them.** It states the whole slice — `01.09. – 05.09.2026 · Ganztags`
 * — and everything that changes it lives behind it. The four day-count presets that used to sit
 * beside it were a machine's vocabulary: "7 T" is how a computer says "letzte Woche", and it ends
 * today, so it cuts the last week in half.
 *
 * The quick list is what somebody actually asks for, one click each, with the period each one
 * resolves to spelled out on the right — "Diese Woche · KW 36" answers "which week am I about to
 * pick" before the click rather than after it.
 *
 * The **level switch is the point**: it says what a click in the grid selects — a day, a week, a
 * month, a quarter. That is why picking a month needs no second control; it is the same act, one
 * level up.
 *
 * The time of day is off until switched on, and when on it means a CONTINUOUS span across midnight
 * — not "these hours on every day", which is what the Tageszeit filter beside this one does. Two
 * controls, because they answer two questions and combine into a third.
 *
 * `Heute` stays a preset rather than a one-day range: the running day carries its own comparison
 * (the same weekday last week, truncated to the same time of day), and a plain range would measure
 * a running lunchtime against a whole previous day.
 */
export function PeriodSwitcher({
  value,
  custom,
  onChange,
  hasTimeWindowFilter = true,
  hasPresetsBeside = false,
}: PeriodSwitcherProps) {
  const { t } = useTranslation('reports')
  const now = useMemo(() => new Date(), [])
  const today = reportDate(now)

  const [open, setOpen] = useState(false)
  const [grain, setGrain] = useState<PeriodGrain>(custom?.grain ?? 'day')
  const [cursor, setCursor] = useState(custom?.from ?? today)
  const [draft, setDraft] = useState<CustomRange | null>(custom ?? null)
  const [draftPreset, setDraftPreset] = useState<PeriodPreset>(value)
  const [anchor, setAnchor] = useState<string | null>(null)
  const [withTime, setWithTime] = useState(!isWholeDayRange(custom))

  const openPanel = (next: boolean) => {
    if (next) {
      // Opening starts from what is applied, so cancelling can put it back untouched.
      setDraft(custom ?? null)
      setDraftPreset(value)
      setGrain(custom?.grain ?? 'day')
      setCursor(custom?.from ?? today)
      setAnchor(null)
      setWithTime(!isWholeDayRange(custom))
    }
    setOpen(next)
  }

  const draftRange: CustomRange | null =
    draftPreset === 'today' ? { from: today, to: today, grain: 'day' } : draft

  const pickNatural = (id: NaturalPeriod) => {
    const range = resolveNaturalPeriod(id, now)
    setAnchor(null)
    // "Heute" is the running day, which is a preset and not a range — see the class note.
    setDraftPreset(id === 'today' ? 'today' : 'custom')
    setDraft({ ...range, fromTime: draft?.fromTime, toTime: draft?.toTime })
    setGrain(range.grain ?? 'day')
    setCursor(range.from)
  }

  const isNaturalActive = (id: NaturalPeriod) => {
    const range = resolveNaturalPeriod(id, now)
    if (id === 'today') return draftPreset === 'today'
    return (
      draftPreset !== 'today' &&
      draft?.from === range.from &&
      draft.to === range.to
    )
  }

  const apply = () => {
    if (draftPreset === 'today') {
      onChange('today')
      setOpen(false)
      return
    }
    if (!draft) return
    onChange('custom', {
      ...draft,
      fromTime: withTime ? (draft.fromTime ?? '00:00') : undefined,
      toTime: withTime ? (draft.toTime ?? '24:00') : undefined,
    })
    setOpen(false)
  }

  return (
    <div className="flex w-full min-w-0 items-center gap-2 lg:w-auto">
      {/* Dropped when the presets sit beside this control: the label would land between the two
          halves of the period and read as if it named only the picker. The presets group carries
          it as its aria-label, and the trigger says "Zeitraum wählen" in as many words. */}
      {hasPresetsBeside ? null : (
        <span className="hidden shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground lg:inline">
          {t('period.label')}
        </span>
      )}

      <Popover open={open} onOpenChange={openPanel}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'h-12 min-w-0 flex-1 justify-start gap-2 font-normal sm:h-9 lg:flex-none',
              hasPresetsBeside &&
                value === 'custom' &&
                'border-primary bg-accent text-accent-foreground',
            )}
          >
            <CalendarRange
              className="h-4 w-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
            <span className="truncate tabular-nums">
              {triggerSummary(t, value, custom, hasPresetsBeside)}
            </span>
            <ChevronDown
              className="ml-auto h-4 w-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-[min(48rem,calc(100vw-2rem))] p-0"
          align="start"
        >
          {/* On a phone the quick list is a chip rail rather than a sidebar: three wrapped rows
              cost the panel ~90px it does not have, and a scrolling rail is the phone gesture. */}
          <div className="flex gap-1.5 overflow-x-auto border-b border-border p-2 sm:hidden">
            {NATURAL_PERIODS.map((id) => (
              <Button
                key={id}
                type="button"
                variant={isNaturalActive(id) ? 'default' : 'outline'}
                className="h-10 shrink-0 rounded-full px-4 font-normal"
                onClick={() => pickNatural(id)}
              >
                {t(`period.natural.${id}`)}
              </Button>
            ))}
          </div>

          <div className="flex items-stretch">
            {/* Nine questions out of ten, one click each. */}
            <div className="hidden w-48 shrink-0 flex-col gap-px border-r border-border bg-muted/30 p-2 sm:flex">
              <div className="px-2 pb-1 pt-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('period.quickLabel')}
              </div>
              {NATURAL_PERIODS.map((id) => {
                const active = isNaturalActive(id)
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => pickNatural(id)}
                    className={cn(
                      'flex h-8 items-center justify-between gap-2 rounded-md px-2 text-left text-sm',
                      active
                        ? 'bg-primary font-medium text-primary-foreground'
                        : 'hover:bg-accent',
                    )}
                  >
                    <span>{t(`period.natural.${id}`)}</span>
                    {/* Which period the click resolves to — before the click, not after it. */}
                    <span
                      className={cn(
                        'text-[11px] tabular-nums',
                        active
                          ? 'text-primary-foreground/70'
                          : 'text-muted-foreground',
                      )}
                    >
                      {naturalPeriodHint(t, id, now)}
                    </span>
                  </button>
                )
              })}
            </div>

            <div className="flex min-w-0 flex-1 flex-col">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 p-2.5">
                {/* What a click in the grid below means. */}
                <ToggleGroup
                  type="single"
                  size="sm"
                  value={grain}
                  onValueChange={(next) => {
                    if (next) setGrain(next as PeriodGrain)
                  }}
                  aria-label={t('period.grainLabel')}
                >
                  {GRAINS.map((option) => (
                    <ToggleGroupItem key={option} value={option}>
                      {t(`period.grains.${option}`)}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>

                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setCursor(pageCursor(cursor, grain, -1))}
                    aria-label={t('period.pageBack')}
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <span className="min-w-32 text-center text-sm font-medium tabular-nums">
                    {pageLabel(t, grain, cursor)}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    disabled={pageIsAtPresent(cursor, grain, now)}
                    onClick={() => setCursor(pageCursor(cursor, grain, 1))}
                    aria-label={t('period.pageForward')}
                  >
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>

              <PeriodGrainGrid
                grain={grain}
                cursor={cursor}
                value={draftRange}
                anchor={anchor}
                onPick={(range, nextAnchor) => {
                  setDraftPreset('custom')
                  setAnchor(nextAnchor)
                  setDraft({
                    ...range,
                    // A period reaching past today is cut at today: nobody wants the rest of the
                    // month counted as a day without revenue.
                    to: range.to > today ? today : range.to,
                    fromTime: draft?.fromTime,
                    toTime: draft?.toTime,
                  })
                }}
                today={today}
              />

              <div className="flex flex-col gap-2.5 border-t border-border/60 p-2.5">
                <div className="flex items-center justify-between gap-3">
                  <label
                    htmlFor="period-with-time"
                    className="flex items-center gap-2 text-sm"
                  >
                    <Clock
                      className="h-4 w-4 text-muted-foreground"
                      aria-hidden="true"
                    />
                    {t('period.time.label')}
                    {withTime ? null : (
                      <span className="text-xs text-muted-foreground">
                        {t('period.time.wholeDays')}
                      </span>
                    )}
                  </label>
                  <Switch
                    id="period-with-time"
                    checked={withTime}
                    onCheckedChange={setWithTime}
                  />
                </div>

                {withTime ? (
                  <div className="flex flex-col gap-1.5">
                    <TimeRow
                      label={t('period.time.from')}
                      day={draftRange?.from}
                      options={FROM_TIMES}
                      value={draftRange?.fromTime ?? '00:00'}
                      onChange={(next) =>
                        setDraft((prev) =>
                          prev ? { ...prev, fromTime: next } : prev,
                        )
                      }
                    />
                    <TimeRow
                      label={t('period.time.to')}
                      day={draftRange?.to}
                      options={TO_TIMES}
                      value={draftRange?.toTime ?? '24:00'}
                      onChange={(next) =>
                        setDraft((prev) =>
                          prev ? { ...prev, toTime: next } : prev,
                        )
                      }
                    />
                    {/* A time on a MULTI-DAY range is the one combination that reliably gets
                        misread: "August, 11:00–14:00" looks like the lunch trade and is in fact
                        the whole month minus two edges — nights included. The generic sentence is
                        not enough there, so the warning names the span and points at the control
                        that answers the question actually being asked. */}
                    {draftRange &&
                    reportDaySpan(draftRange.from, draftRange.to) > 1 ? (
                      <p className="rounded-md bg-warning/10 p-2 text-xs leading-5 text-warning-foreground">
                        {t(
                          hasTimeWindowFilter
                            ? 'period.time.spanWarning'
                            : 'period.time.spanWarningNoFilter',
                          {
                            days: reportDaySpan(draftRange.from, draftRange.to),
                          },
                        )}
                      </p>
                    ) : (
                      <p className="text-xs leading-5 text-muted-foreground">
                        {t(
                          hasTimeWindowFilter
                            ? 'period.time.hint'
                            : 'period.time.hintNoFilter',
                        )}
                      </p>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-border bg-muted/30 p-2.5">
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-medium tabular-nums">
                {draftRange
                  ? draftSummary(t, draftPreset, draftRange, withTime)
                  : t('period.custom.hint')}
              </span>
              {/* What the Δ column will be measured against — the one thing about a picked period
                  that the period itself does not say. */}
              {draftRange ? (
                <span className="truncate text-xs tabular-nums text-muted-foreground">
                  {compareLine(t, draftPreset, draftRange)}
                </span>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOpen(false)}
              >
                {t('period.custom.cancel')}
              </Button>
              <Button
                size="sm"
                onClick={apply}
                // A half-picked range has an open anchor: applying it would silently report the
                // single day the first click landed on.
                disabled={!draftRange || anchor != null}
              >
                {t('period.custom.apply')}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

function TimeRow({
  label,
  day,
  options,
  value,
  onChange,
}: {
  label: string
  /** The end of the range this time belongs to — so "von 06:00" says *which* morning. */
  day: string | undefined
  options: Array<string>
  value: string
  onChange: (next: string) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="w-8 shrink-0 text-xs text-muted-foreground">
        {label}
      </span>
      {day ? (
        <span className="flex h-8 shrink-0 items-center rounded-md border border-input px-2 text-sm tabular-nums">
          {formatDate(`${day}T12:00:00Z`)}
        </span>
      ) : null}
      <input
        type="time"
        value={value === '24:00' ? '23:59' : value}
        onChange={(event) => onChange(event.target.value)}
        className="h-8 w-28 rounded-md border border-input bg-background px-2 text-sm tabular-nums"
      />
      {options.map((option) => (
        <Button
          key={option}
          type="button"
          variant={value === option ? 'default' : 'outline'}
          size="sm"
          className="h-8 px-2.5 tabular-nums"
          onClick={() => onChange(option)}
        >
          {option}
        </Button>
      ))}
    </div>
  )
}

type Translate = (key: string, options?: Record<string, unknown>) => string

/** `07.09.2026 · Ganztags` — the whole slice, on the one control that owns it. */
function triggerSummary(
  t: Translate,
  value: PeriodPreset,
  custom: CustomRange | null | undefined,
  hasPresetsBeside: boolean,
): string {
  if (value !== 'custom' || !custom) {
    // Presets beside it already say which one is on; repeating it here would be the second
    // control claiming to state the period.
    if (hasPresetsBeside) return t('period.custom.label')
    // The running day says so by name: a bare date would hide that it is still going.
    const preset = value === 'custom' ? 'today' : value
    return `${t(`period.presets.${preset}`)} · ${t('period.summary.wholeDays')}`
  }
  const time = isWholeDayRange(custom)
    ? t('period.summary.wholeDays')
    : `${custom.fromTime ?? '00:00'}–${custom.toTime ?? '24:00'}`
  return `${describeRange(t, { ...custom, fromTime: undefined, toTime: undefined })} · ${time}`
}

function draftSummary(
  t: Translate,
  preset: PeriodPreset,
  range: CustomRange,
  withTime: boolean,
): string {
  const base =
    preset === 'today'
      ? t('period.presets.today')
      : describeRange(t, { ...range, fromTime: undefined, toTime: undefined })
  const time = withTime
    ? `${range.fromTime ?? '00:00'}–${range.toTime ?? '24:00'}`
    : t('period.summary.wholeDays')
  return `${base} · ${time}`
}

/**
 * The window the Δ column will be measured against.
 *
 * The running day is the exception and half the reason this line exists: it compares against the
 * same weekday one week earlier, truncated to the same time of day, and nothing about the picked
 * period says so.
 */
function compareLine(
  t: Translate,
  preset: PeriodPreset,
  range: CustomRange,
): string {
  if (preset === 'today') {
    return t('period.summary.compareWeekday', {
      day: formatDate(`${shiftReportDate(range.from, -7)}T12:00:00Z`),
    })
  }
  const days = reportDaySpan(range.from, range.to)
  const to = shiftReportDate(range.from, -1)
  const from = shiftReportDate(to, -(days - 1))
  return t('period.summary.comparePrevious', {
    from: formatDate(`${from}T12:00:00Z`),
    to: formatDate(`${to}T12:00:00Z`),
  })
}

/** `September 2026`, `KW 31–36`, `2026` — what the grid below is currently showing. */
function pageLabel(t: Translate, grain: PeriodGrain, cursor: string): string {
  if (grain === 'day') {
    return `${t(`period.months.${Number(cursor.slice(5, 7)) - 1}`)} ${cursor.slice(0, 4)}`
  }
  if (grain === 'week') {
    return t('period.weekRange', {
      from: isoWeekOf(weekWindow(cursor)[0].from),
      to: isoWeekOf(cursor),
    })
  }
  return cursor.slice(0, 4)
}
